import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Link } from 'react-router-dom';
import { Calendar, User, ChevronRight } from 'lucide-react';
import T from '../T';

const HIDDEN_ARTICLE_TITLE = 'Family Law Support for Families Navigating Change';

interface Article {
  id: string;
  title: string;
  content: string;
  authorName: string;
  status?: 'pending' | 'approved';
  createdAt: any;
  imageSrc?: string;
  imageAlt?: string;
}

const BlogSection: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, 'articles'),
      where('status', '==', 'approved'),
      orderBy('createdAt', 'desc'),
      limit(2)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const articlesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Article[];
      setArticles(articlesData);
      setLoading(false);
    }, () => {
      setArticles([]);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) return null;
  const visibleArticles = articles
    .filter((article) => article.title !== HIDDEN_ARTICLE_TITLE)
    .map((article) => ({
      ...article,
      linkTo: '/articles'
    }));

  if (visibleArticles.length === 0) return null;

  return (
    <section style={{ padding: '80px 0', backgroundColor: 'var(--white)' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h2 style={{ fontSize: '2.5rem', color: 'var(--primary-color)', marginBottom: '1rem' }}><T>Recent Articles & Insights</T></h2>
          <div style={{ width: '60px', height: '4px', backgroundColor: 'var(--secondary-color)', margin: '0 auto' }}></div>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '2.5rem' 
        }}>
          {visibleArticles.map((article) => (
            <div key={article.id} style={{
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              transition: 'var(--transition)',
              border: '1px solid #eee'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-5px)')}
            onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              {typeof article.imageSrc === 'string' && article.imageSrc ? (
                <img
                  src={article.imageSrc}
                  alt={typeof article.imageAlt === 'string' && article.imageAlt ? article.imageAlt : article.title}
                  loading="lazy"
                  decoding="async"
                  style={{ width: '100%', height: '240px', objectFit: 'cover', display: 'block' }}
                />
              ) : null}
              <div style={{ padding: '2rem', flex: 1 }}>
                <h3 style={{ 
                  fontSize: '1.25rem', 
                  color: 'var(--primary-color)', 
                  marginBottom: '1rem',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  <T>{article.title}</T>
                </h3>
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem', color: '#666', marginBottom: '1.5rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <User size={14} /> <T>{article.authorName}</T>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <Calendar size={14} /> 
                    {article.createdAt?.toDate ? article.createdAt.toDate().toLocaleDateString() : ''}
                  </span>
                </div>
                <p style={{ 
                  color: '#444', 
                  lineHeight: '1.6', 
                  marginBottom: '1.5rem',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  <T>{article.content}</T>
                </p>
                <Link to={article.linkTo} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.3rem', 
                  color: 'var(--secondary-color)', 
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  <T>Read Full Article</T> <ChevronRight size={16} />
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '4rem' }}>
          <Link to="/articles" className="btn btn-primary">
            <T>View All Articles</T>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
